import { DeveloperHelperComponent } from '@/components/developer-helper/developer-helper';
import { HighchartsChartComponent } from '@/components/highcharts-chart/highcharts-chart';
import { LoadingAndErrorComponent } from '@/components/loading-and-error/loading-and-error';
import { LoadingAndErrorResourceBindingDirective } from '@/components/loading-and-error/loading-and-error-resource-binding.directive';
import { AdvizeoTooltipDirective } from '@/directives/advizeo-tooltip/advizeo-tooltip.directive';
import { UniverseService } from '@/domain/universes/universe.service';
import type { UniverseAggregatedDeliveryPointTimeseriesPoint } from '@/interfaces/Timeseries.interface';
import type { YearlyIndicatorSettings, YearlyIndicatorTranslationKeys } from '@/interfaces/Widget.interface';
import { CsvExportProvider } from '@/providers/csv-export/csv-export';
import { DashboardingProvider } from '@/providers/dashboarding.provider';
import { DataProvider } from '@/providers/data/data.provider';
import { HighchartsStaticProvider } from '@/providers/highcharts/highcharts.static.provider';
import { UnitProvider } from '@/providers/unit/unit.provider';
import { WidgetSize } from '@/providers/widgets.contants';
import type { TooltipFormatterContext } from '@/ui/chart/builder/parts/tooltip.utils';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionActionsDirective } from '@/ui/section/section-actions/ui-section-actions';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { I18nService } from '@/utils/i18n/i18n.service';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { groupBy } from '@/utils/toolkit/object/group-by.utils';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { SeriesOptionsType } from 'highcharts';
import moment from 'moment';
import { map, of } from 'rxjs';
import { AbstractWidgetClass } from '../AbstractWidget.class';

@Component({
    selector: 'widget-yearly-indicator',
    imports: [
        HighchartsChartComponent,
        LoadingAndErrorComponent,
        LoadingAndErrorResourceBindingDirective,
        UiSectionComponent,
        UiSectionActionsDirective,
        UiIconButtonComponent,
        AdvizeoTooltipDirective,
        TranslatePipe,
        UiSectionContentComponent,
        DeveloperHelperComponent
    ],
    templateUrl: './widget-yearly-indicator.component.html',
    styleUrl: './widget-yearly-indicator.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class WidgetYearlyIndicatorComponent extends AbstractWidgetClass<YearlyIndicatorTranslationKeys, YearlyIndicatorSettings> {
    private _dashboardingProvider = inject(DashboardingProvider);
    private _dataProvider = inject(DataProvider);
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _universeService = inject(UniverseService);
    private _i18n = inject(I18nService);
    private _referenceListsService = inject(ReferenceListsService);
    private _unitProvider = inject(UnitProvider);
    private _csvExportProvider = inject(CsvExportProvider);
    size = WidgetSize.FULL;

    classificationNodeId = toSignal(this._dashboardingProvider.getSelectedNodeId$());
    isEstimatedIgnoredOnWidgets = toSignal(this._dashboardingProvider.isEstimatedIgnoredOnWidgets$());

    private _yearlyDeliveryPointTimeseriesPoint: UniverseAggregatedDeliveryPointTimeseriesPoint[] = [];

    chartConfig = rxResource({
        params: computed(() => ({
            classificationNodeId: this.classificationNodeId(),
            isEstimatedIgnoredOnWidgets: this.isEstimatedIgnoredOnWidgets()
        })),
        stream: ({ params: { classificationNodeId, isEstimatedIgnoredOnWidgets } }) => {
            if (isNullish(classificationNodeId)) return of(undefined);

            return this._dataProvider
                .getYearlyDeliveryPointTimeseriesPoint$(
                    classificationNodeId,
                    [this.config.timeseriesIndicator],
                    moment('1970-01-01'),
                    moment().add(10, 'years'), // null values are not accepted...
                    isEstimatedIgnoredOnWidgets
                )
                .pipe(
                    map(data => {
                        this._yearlyDeliveryPointTimeseriesPoint = data;
                        return this.generateHighchartConfig(data);
                    })
                );
        }
    });

    generateHighchartConfig(data: UniverseAggregatedDeliveryPointTimeseriesPoint[]): Highcharts.Options {
        const series = this.generateSeries(data);

        const baseUnitCode = this._unitProvider.getDeliveryPointTimeseriesUnitCode(this.config.timeseriesIndicator);
        const valueFormatter = this._unitProvider.formatterFactory(
            baseUnitCode,
            this._unitProvider.getBestConversion(data.map(d => d.value).filter(isNotNullish), baseUnitCode)
        );

        return this._highchartsStaticProvider.buildConfig(
            this._highchartsStaticProvider.DISABLE_ANIMATIONS,
            this._highchartsStaticProvider.REFLOW_ON_CONTAINER_RESIZE,
            this._highchartsStaticProvider.COLUMN_MIN_SIZE_5,
            {
                xAxis: {
                    type: 'category',
                    labels: {
                        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                            if (typeof this.value === 'number') {
                                return this.value;
                            }
                            return Temporal.PlainDate.from(this.value).year;
                        }
                    }
                },
                yAxis: {
                    reversedStacks: false,
                    labels: {
                        formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
                            return valueFormatter(this.value as number);
                        }
                    }
                },
                tooltip: {
                    formatter(this: TooltipFormatterContext) {
                        return `${Temporal.PlainDate.from(this.point.name).year}<br/>  ${this.point.series.name}: <b> ${this.point.y ? valueFormatter(this.point.y) : '--'} </b>`;
                    }
                },
                plotOptions: {
                    column: {
                        stacking: 'normal'
                    }
                },
                legend: {
                    enabled: true
                },
                series
            }
        );
    }

    generateSeries(data: UniverseAggregatedDeliveryPointTimeseriesPoint[]): Highcharts.SeriesOptionsType[] {
        const isEstimated = !this.isEstimatedIgnoredOnWidgets();
        const series: Highcharts.SeriesOptionsType[] = [];

        const measuredData = data.filter(d => d.metric === 'measured');
        const measureDataByUniverse = groupBy(measuredData, measuredData => measuredData.universeId);

        Array.from([...measureDataByUniverse.entries()].sort()).forEach(([universeId, values]) => {
            const universe = this._universeService.getUniverseR(universeId);

            series.push({
                name: `${this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIVERSE, universeId)} (${this._i18n.translateR('WIDGET.LEGEND.MEASURED')})`,
                type: 'column',
                color: universe?.colorMain,
                data: values
                    .sort((a, b) => (a.date < b.date ? -1 : 1))
                    .map(v => {
                        return {
                            name: v.date,
                            x: Temporal.PlainDate.from(v.date).year,
                            y: v.value
                        };
                    }),
                index: (universe?.rank ?? 0) + 0
            });
        });

        if (isEstimated) {
            const measuredData = data.filter(d => d.metric === 'estimated');
            const measureDataByUniverse = groupBy(measuredData, measuredData => measuredData.universeId);

            Array.from([...measureDataByUniverse.entries()].sort()).forEach(([universeId, values]) => {
                const universe = this._universeService.getUniverseR(universeId);
                series.push({
                    name: `${this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIVERSE, universeId)} (${this._i18n.translateR('WIDGET.LEGEND.ESTIMATED')})`,
                    type: 'column',
                    color: universe?.colorMain ? this._highchartsStaticProvider.generateDiagonalStripesPattern(universe?.colorMain) : '',
                    data: values
                        .sort((a, b) => (a.date < b.date ? -1 : 1))
                        .map(v => {
                            return {
                                name: v.date,
                                y: v.value
                            };
                        }),
                    index: (universe?.rank ?? 0) + 0.5
                });
            });
        }
        return series;
    }

    exportDataAsCsv() {
        const valuesToExport = Array.from([
            ...groupBy(this._yearlyDeliveryPointTimeseriesPoint, measuredData => measuredData.date).entries()
        ])
            .map(([date, values]) => {
                return {
                    date,
                    ...values.reduce((acc, current) => {
                        const universeTranslation = `${this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIVERSE, current.universeId)} (${this._i18n.translateR('WIDGET.LEGEND.' + current.metric.toUpperCase())})`;
                        return { ...acc, [universeTranslation]: current.value };
                    }, {})
                };
            })
            .sort((a, b) => (a.date < b.date ? -1 : 1));

        const seriesName = (this.chartConfig.value()?.series ?? [])
            .map((s: SeriesOptionsType) => s.name)
            .filter(isNotNullish)
            .map((serieName: string) => {
                return {
                    header: serieName,
                    value: (line: any) => line[serieName]
                };
            });

        this._csvExportProvider.downloadCsvFromDataSource(valuesToExport, [
            {
                header: 'Date',
                value: line => line.date
            },
            ...seriesName
        ]);
    }
}
